/**
 * Text-shape helpers shared by every agent. Project rule
 * (CLAUDE.md): em-dashes and en-dashes used as punctuation are
 * banned in user-facing copy and LLM output. Don't beg models to
 * follow that rule; enforce it after the fact.
 */
// Shared plain-language directive, built on all four ISO 24495-1:2023
// governing principles (relevant, findable, understandable, usable), not just
// clarity. Concatenated into every copy agent's system prompt. The reader is a
// person looking after their health, often tired or busy, reading on a phone
// in a spare moment. Plain language is measured by whether THIS reader can act
// on it, not by readability formulas, so these are judgement calls, not
// mechanical rules.
export const PLAIN_LANGUAGE_RULE = `WRITE IN PLAIN LANGUAGE (ISO 24495-1:2023).
Plain language means this reader can find what they need, understand it the first time, and act on it. Hold every user-facing line to the four principles:

RELEVANT (give THIS reader what they need, now):
- Write for this specific person and this moment, not a general audience. Lead with what matters most to them.
- Ground every line in something real about them: a signal you can see, the moment that prompted this, what they told you. If a line would read exactly the same sent to any random person, it is filler. Cut it or make it specific.
- Include only what they need to act on; leave the rest out.

FINDABLE (put the point where they will see it):
- Most important thing first. One clear ask, never buried.

UNDERSTANDABLE (they get it on the first read):
- Everyday words, not clinical or technical ones. Same word for the same thing; do not swap in synonyms for a concept they are tracking.
- One idea per sentence, short (aim for 15 words or fewer). Active voice, real verbs ("Take three slow breaths", not "a few slow breaths can be taken").
- Plain numbers the way a person speaks ("about six and a half hours", never "6.56"). Translate signals into meaning; never paste raw values or units (bpm, ms, RMSSD, mg/dL) or clinical body-system terms ("nervous system") where an everyday phrase works.
- Say what to do, not what to avoid. Concrete, never abstract. No hype, idiom, or metaphor ("good groove", "hold the rhythm", "buys time back"). Say it once.
- No system words: never "objective", "lever", "tests the X objective", "your plan's focus". Talk about their day, not the machinery.

USABLE (it leaves them able to act, and supported):
- End on the next small step they can actually take.
- Respectful, supportive tone: a calm companion, not a clinician giving orders or a coach hyping them up. Encourage; never alarm, shame, lecture, or guilt. Mark a win plainly and warmly; treat a missed step without judgement.

Test each line: a tired person should, on the first read, know what to do, feel it was written for them, and feel supported.`;
// Shared locale rule for every user-facing agent. HealthOS members are
// global; the models default to US idiom and units unless told otherwise.
export const INTERNATIONAL_ENGLISH_RULE = `WRITE INTERNATIONAL ENGLISH.
This user may live anywhere. Hold every user-facing line to this:
- No region-bound idioms, places, or routines: never reference a street layout, store type, sport, season, or meal convention that only exists in one country. Phrase actions so they work anywhere ("a short walk outside", not a culture-specific route or venue).
- Units: mirror the units the user's own signals use; never convert to another system. When no signal sets the unit, use metric with the spoken form.
- Spelling and dates stay neutral: avoid region-marked spellings where a neutral word exists, write dates as the day and month in words.`;
// Shared rationale rules for the "Why now?" / "Why this?" caption every
// surfacing agent writes (Heartbeat, Microstep Generator). Concatenated into
// each prompt so the captions sound like one product and the grounding guards
// (most-recent value, time framing) can't drift between agents.
export const RATIONALE_RULE = `RATIONALE RULES (hold every rationale to this bar):
- Second person. Address the user as "you"; never the third person ("the user has...") - that is a bug.
- Two short sentences, not three. Name what you noticed, then the action, then ONE consequence, and stop. Exactly one payoff: never chain benefits ("so... which helps... fuelling... so you can..."). If you wrote a second "so" or a second benefit, delete everything after the first. A human coach names one reason and moves on; stacking three is the tell of a machine. Warm and grounded, no bullet points, no labels.
- Recognition, never a receipt. Name what you noticed as a person would ("rough afternoon", "short night", "long day"), NEVER as a tally of their data or of what they did or failed to do ("four hours seated", "you opened the app late", "no steps logged", "missed steps"). To someone struggling, a receipt reads as a scoreboard and makes them withdraw. When the moment is steady you may open with what you noticed; when it is fragile (frozen, ashamed, disengaged, or defending time they have claimed as their own), open with the offer (the door) and keep the data out of the opening line.
- The action must be the answer to what you noticed, not a generic move the same words could justify for anyone. If the opening cause and the action are not clearly linked, the rationale has not landed.
- Offer it, never order it. Assume they want the outcome and hand them the step. No commands, no urgency, no data-shaming, no hype; they can always say not now.
- Cite signals in plain language ("4 hours last night", "back-to-back meetings"), never clinical labels ("sleep debt", "HRV deficit").
- When a signal kind has several values, quote the MOST RECENT one (the latest timestamp); never an older reading when a newer one exists.
- Match the time of day you are given. Do not anchor to a moment that has passed or has not arrived (no "before bed" in the morning, no "this morning" in the evening); frame a future lever explicitly as "for later".
- Describe what you noticed; do not label the person ("you're stressed").`;
// Soft variants of the three rules for Claude-family prompts. Same substance and
// the same grounding guards (most-recent value, time framing), in affirmative,
// no-caps phrasing, because the newer Claude models over-comply with stacked
// caps and "never" walls. gpt-oss prompts keep the originals above.
export const PLAIN_LANGUAGE_RULE_SOFT = `Write in plain language (ISO 24495-1:2023). The reader is a person looking after their health, often tired or busy, reading on a phone. Aim for copy they can find, understand on the first read, and act on.
- Relevant: write for this specific person and this moment. Ground every line in something real about them: a signal you can see, the moment that prompted this, what they told you. If a line would read the same to anyone, make it specific or cut it. Include only what they need to act on.
- Findable: lead with the most important thing; keep the one ask clear and unburied.
- Understandable: everyday words over clinical or technical ones, and the same word for a concept they are tracking. One idea per sentence, short (aim for 15 words or fewer), active voice with real verbs. Speak numbers the way a person does ("about six and a half hours"). Translate signals into meaning rather than pasting raw values or units (bpm, ms, RMSSD, mg/dL) or body-system terms. Say what to do, concretely. Say it once. Keep it free of hype, idiom, and metaphor, and of system words like "objective", "lever", or "your plan's focus".
- Usable: end on the next small step they can take. Keep a calm companion's tone, supportive rather than clinical or hyped; encourage, mark a win warmly, and treat a missed step without judgement.

Test each line: a tired person should, on the first read, know what to do, feel it was written for them, and feel supported.`;
export const INTERNATIONAL_ENGLISH_RULE_SOFT = `Write international English. This user may live anywhere, so keep every line portable:
- Phrase actions so they work anywhere ("a short walk outside" rather than a culture-specific route, venue, sport, season, or meal convention).
- Mirror the units the user's own signals use; when no signal sets the unit, use metric in spoken form.
- Keep spelling and dates neutral; write dates as the day and month in words.`;
export const RATIONALE_RULE_SOFT = `Hold every rationale to this bar:
- Second person: address the user as "you" (writing about "the user" is a bug).
- Two short sentences: name what you noticed, then the action, then one payoff, and stop. Keep exactly one payoff; if a second "so" or second benefit appears, end the sentence at the first. Warm and grounded, no bullet points or labels.
- Recognition, not a receipt: name what you noticed as a person would ("rough afternoon", "short night"), rather than a tally of their data ("four hours seated", "no steps logged"); a receipt reads as a scoreboard. When the moment is fragile (frozen, ashamed, disengaged, or protective of their time), open with the offer and keep the data out of the first line.
- Make the action the answer to what you noticed, not a generic move the same words could justify for anyone.
- Offer it rather than order it: assume they want the outcome and hand them the step. No commands, urgency, data-shaming, or hype; they can always say not now.
- Cite signals in plain language ("4 hours last night", "back-to-back meetings") rather than clinical labels.
- When a signal kind has several values, quote the most recent one (the latest timestamp), not an older reading.
- Match the time of day you are given; frame a future lever explicitly as "for later" rather than anchoring to a moment that has passed or not arrived.
- Describe what you noticed rather than labelling the person ("you're stressed").`;
const PUNCTUATION_DASH = /\s*[\u2014\u2013]\s*/g;
/**
 * Replace every em-dash (U+2014) and en-dash (U+2013) used as
 * punctuation with " - " (space-hyphen-space), collapsing any
 * surrounding whitespace. CLAUDE.md lists ` - ` as the allowed
 * fallback when richer rephrasing isn't available.
 *
 * Numeric ranges that already use a regular hyphen ("5-7 days")
 * are untouched; only the typographic dashes match.
 */
export function stripDashes(input) {
    return input.replace(PUNCTUATION_DASH, " - ");
}
// Hyphen-like Unicode characters that should collapse to a plain ASCII hyphen:
// U+2010 hyphen, U+2011 non-breaking hyphen, U+2012 figure dash. Models
// (notably gpt-oss) emit the non-breaking hyphen inside compound words like
// "fifty‑eight"; we want a normal hyphen on user-facing surfaces. This is
// distinct from stripDashes, which turns em/en dashes into a spaced " - ".
const UNICODE_HYPHEN = /[\u2010\u2011\u2012]/g;
export function normaliseHyphens(input) {
    return input.replace(UNICODE_HYPHEN, "-");
}
// Models mix these; the product is straight everywhere, so generated copy is too.
export function normaliseQuotes(input) {
    return input
        .replace(/[\u2018\u2019\u201a\u201b\u02bc]/g, "'")
        .replace(/[\u201c\u201d\u201e\u201f]/g, '"');
}
// Single-codepoint normalisers only, so this is safe on a streamed fragment.
export function normaliseDisplayText(input) {
    return normaliseQuotes(normaliseHyphens(stripDashes(input)));
}
// Full battery for settled text; the multi-word rewrites need whole words.
// Spelling is not part of it: it needs the member's preference, composed on top.
export function sanitiseDisplayText(input) {
    return fixArticleAgreement(stripSystemSpeak(normaliseDisplayText(input)));
}
// A space-flanked dash (the em-dash brand fallback, ` - `, plus any stray em/en
// dash) makes Fish Audio stumble or speak the hyphen. Turn it into a comma, a
// natural spoken pause, before sending text to TTS. Word-internal hyphens
// ("fine-tune", "wind-down") and numeric ranges ("5-7") have no flanking spaces,
// so they are left for the voice to pronounce normally.
const SPOKEN_DASH = /\s+[\u2014\u2013-]\s+/g;
// Fish Audio reads compact magnitudes and symbols literally: "$31.1M" becomes
// "dollars thirty one point one em", "$8 K" ends on the letter K, "3.6x" says
// "ex". Rewrite the money, multiplier, and clinical-unit shapes our briefs use
// into words the voice speaks naturally. The numerals are left as digits (the
// voice reads "31.1 million dollars" correctly); only the symbol and suffix are
// spelled out. Spoken layer only: the displayed copy keeps the compact form.
const MAGNITUDE_WORD = { k: "thousand", m: "million", b: "billion" };
const CURRENCY_MAGNITUDE = /\$\s?(\d[\d,]*(?:\.\d+)?)\s*([kmb])\b/gi;
const CURRENCY_PLAIN = /\$\s?(\d[\d,]*(?:\.\d+)?)/g;
const MMHG = /(\d[\d,]*(?:\.\d+)?)\s?mmhg\b/gi;
const MULTIPLIER = /(\d(?:\.\d+)?)\s?[x\u00d7](?=$|[\s.,;:!?])/gi;
export function speechifyNumbers(input) {
    return input
        .replace(CURRENCY_MAGNITUDE, (_m, num, suffix) => `${num} ${MAGNITUDE_WORD[suffix.toLowerCase()]} dollars`)
        .replace(CURRENCY_PLAIN, (_m, num) => `${num} dollars`)
        .replace(MMHG, (_m, num) => `${num} millimeters of mercury`)
        .replace(MULTIPLIER, (_m, num) => `${num} times`);
}
export function speechSafe(input) {
    const dashed = input
        .replace(SPOKEN_DASH, ", ")
        .replace(/,\s*,/g, ",")
        .replace(/[ \t]{2,}/g, " ");
    return speechifyNumbers(dashed);
}
// Raw clinical/metric jargon agents sometimes leave in user-facing copy. The
// reader sees everyday words, never "HRV" or "your nervous system". Shared by
// the microstep and heartbeat sanitisers so the two cannot drift. Word-boundaried
// so compounds stay intact.
const CLINICAL_TO_PLAIN = [
    [/\bheart rate variability\b/gi, "recovery"],
    [/\bHRV\b/g, "recovery"],
    [/\bRMSSD\b/gi, "recovery"],
    [/\bVO\s*[- ]?2(?:\s*max)?\b/gi, "fitness"],
    [/\bVOtwo(?:\s*max)?\b/gi, "fitness"],
    [/\byour nervous system\b/gi, "your body"],
    [/\bnervous system\b/gi, "body"],
];
// System-speak the agents lean on that no one says at a kitchen table. "The
// read" is our instrumentation, not the reader's day; rewrite it with verb
// agreement intact. Part of the system-word rewrite family alongside
// CLINICAL_TO_PLAIN, kept separate so surfaces with value-anchored clinical
// terms (the snapshot's HRV drivers) can adopt it alone.
const SYSTEM_SPEAK_TO_PLAIN = [
    [/\bresumes\s+the\s+read\b/gi, "picks this back up"],
    [/\bresume\s+the\s+read\b/gi, "pick this back up"],
    [/\bresuming\s+the\s+read\b/gi, "picking this back up"],
    [/\brestores\s+the\s+read\b/gi, "brings this back"],
    [/\brestore\s+the\s+read\b/gi, "bring this back"],
    [/\brestoring\s+the\s+read\b/gi, "bringing this back"],
];
export function stripSystemSpeak(value) {
    let out = value;
    for (const [pattern, replacement] of SYSTEM_SPEAK_TO_PLAIN) {
        out = out.replace(pattern, replacement);
    }
    return out;
}
export function toPlainLanguage(value) {
    let out = value;
    let replaced = false;
    for (const [pattern, replacement] of CLINICAL_TO_PLAIN) {
        const next = out.replace(pattern, replacement);
        if (next !== out) {
            out = next;
            replaced = true;
        }
    }
    if (!replaced)
        return out;
    return out.replace(/(^|[.!?]\s+)([a-z])/g, (_m, lead, ch) => lead + ch.toUpperCase());
}
// SSML tags we let the brief composer emit for the TTS voice (chiefly
// `<break time="0.5s" />` for deliberate pauses). They must never reach a
// user-facing surface, so the display path strips them. Ellipses and ordinary
// punctuation are spoken cues, not tags, and are left untouched.
const SSML_TAG = /<\s*\/?\s*(?:break|phoneme|prosody|emphasis|say-as|lang|sub|p|s)\b[^>]*>/gi;
// Models occasionally echo prompt fencing or invent markup; nothing an agent
// writes for a user should contain angle-bracket tags.
export function stripMarkup(input) {
    return input
        .replace(/<\/?[a-zA-Z][^<>]*>/g, " ")
        .replace(/ {2,}/g, " ")
        .replace(/ ([.,;:!?])/g, "$1")
        .trim();
}
export function stripSsml(input) {
    return input
        .replace(SSML_TAG, " ")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/\s+([.,!?;:])/g, "$1")
        .trim();
}
/*
 * Register lint (Law 3): the skeptic register bans wellness cheer in OUR
 * generated copy (brief, push). Code enforces it after the fact, same as
 * stripDashes; never applied to user-authored content.
 */
const SIGNOFF_PHRASES = /\b(?:take good care|rest easy|you['’]re doing great)\b(?:\s+and\b)?[.,!]?\s*/gi;
const HYPE_PHRASES = /\b(?:great job|well done|you['’]ve got this|(?:you['’]re\s+)?crushing it|keep (?:it|this) up|keep the momentum)\b(?:\s+and\b)?[.,!]?\s*/gi;
const REGISTER_WORDS = /\b(?:journey|wellness|self[-\s]?care|amazing|deserv(?:e|es|ed|ing))\b/i;
/** Off-voice words: absent from the reviewed stem library, common in model output.
 *  "Mindful" is deliberately not here; the stems use it. Stems, so inflections are covered. */
export const OFF_VOICE_STEMS = [
    "ritual",
    "embrac",
    "cultivat",
    "nurtur",
    "cherish",
    "savou?r",
    "vibran",
    "vitalit",
    "intentional",
    "gift",
];
const OFF_VOICE = new RegExp(`\\b(${OFF_VOICE_STEMS.join("|")})\\w*`, "gi");
// The stems are regex, so a prompt cannot print them raw: "savou?r" told the
// model not to write "savr".
export const OFF_VOICE_PROMPT_WORDS = OFF_VOICE_STEMS.map((stem) => stem.replace(/\?/g, ""));
/** Off-voice words in the copy, lowercased and deduped. */
export function offVoiceWords(copy) {
    return [...new Set((copy.match(OFF_VOICE) ?? []).map((w) => w.toLowerCase()))];
}
function tidyAfterStrip(s) {
    return s
        .replace(/\s{2,}/g, " ")
        .replace(/\s+([.,;:!?])/g, "$1")
        .replace(/,\s*([.!?])/g, "$1")
        .replace(/,\s*$/g, ".")
        .trim();
}
/**
 * Strip banned register from generated copy: sign-off sentiment and hype
 * phrases lose the phrase (the rest of the sentence survives), exclamation
 * marks become periods, and a sentence built on banned vocabulary (journey,
 * wellness, self-care, amazing, deserve) is removed whole. Never empties the
 * text: an all-banned input is kept and tagged register.strip_aborted so
 * telemetry alarms instead of shipping a blank field.
 */
export function stripBannedRegister(input) {
    const hits = [];
    let out = input;
    if (/!/.test(out)) {
        hits.push("register.exclamation");
        out = out.replace(/!+/g, ".");
    }
    SIGNOFF_PHRASES.lastIndex = 0;
    if (SIGNOFF_PHRASES.test(out)) {
        hits.push("register.signoff");
        out = tidyAfterStrip(out.replace(SIGNOFF_PHRASES, " "));
    }
    HYPE_PHRASES.lastIndex = 0;
    if (HYPE_PHRASES.test(out)) {
        hits.push("register.hype");
        out = tidyAfterStrip(out.replace(HYPE_PHRASES, " "));
    }
    if (REGISTER_WORDS.test(out)) {
        hits.push("register.vocab");
        const kept = out
            .split(/(?<=[.!?])\s+|\n+/)
            .filter((sentence) => !REGISTER_WORDS.test(sentence))
            .join(" ")
            .replace(/\s{2,}/g, " ")
            .trim();
        out = kept;
    }
    if (out.trim().length === 0 && input.trim().length > 0) {
        return { text: input, hits: [...hits, "register.strip_aborted"] };
    }
    return { text: out, hits };
}
const VOWEL_SOUND_WORDS = new Set([
    "eight",
    "eighteen",
    "eighty",
    "eleven",
    "hour",
    "hours",
    "hourly",
]);
const CONSONANT_SOUND_WORDS = new Set([
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "nine",
    "ten",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "nineteen",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "ninety",
    "hundred",
    "thousand",
    "million",
    "half",
]);
function numeralStartsWithVowelSound(numeral) {
    const digits = numeral.replace(/,/g, "").split(".")[0] ?? "";
    if (digits.length === 0)
        return false;
    // The spoken first word comes from the leading thousands-group: 8 "eight",
    // 11 "eleven", 18 "eighteen", 80-89 "eighty", 800-899 "eight hundred".
    const lead = digits.slice(0, ((digits.length - 1) % 3) + 1);
    return /^(?:8\d{0,2}|11|18)$/.test(lead);
}
const ARTICLE_BEFORE_TOKEN = /\b(a|an)(\s+\*{0,2})(\d[\d,]*(?:\.\d+)?|[A-Za-z]+)/gi;
/**
 * Correct "a" vs "an" ONLY before a numeral or an allowlisted number-word
 * whose spoken form is known ("a 8.7h" to "an 8.7h", "an 7h" to "a 7h",
 * "a hour" to "an hour"). Any other word is left untouched, so ordinary
 * prose ("a banana") can never be mangled. Generated copy only; never run
 * on user-authored content.
 */
export function fixArticleAgreement(input) {
    return input.replace(ARTICLE_BEFORE_TOKEN, (match, article, gap, target) => {
        let vowel;
        if (/^\d/.test(target)) {
            vowel = numeralStartsWithVowelSound(target);
        }
        else {
            const word = target.toLowerCase();
            vowel = VOWEL_SOUND_WORDS.has(word) ? true : CONSONANT_SOUND_WORDS.has(word) ? false : null;
        }
        if (vowel === null)
            return match;
        const want = vowel ? "an" : "a";
        if (article.toLowerCase() === want)
            return match;
        const cased = /^[A-Z]/.test(article) ? want.charAt(0).toUpperCase() + want.slice(1) : want;
        return cased + gap + target;
    });
}
function decodeIngredientText(s) {
    return s
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/g, "'")
        .replace(/&frac14;/gi, "1/4")
        .replace(/&frac12;/gi, "1/2")
        .replace(/&frac34;/gi, "3/4");
}
export function parseIngredientLines(raw) {
    const asLines = Array.isArray(raw)
        ? raw
        : typeof raw === "string"
            ? raw
                .replace(/<\/li>/gi, "\n")
                .replace(/<br\s*\/?>/gi, "\n")
                .replace(/<\/p>/gi, "\n")
                .split(/\r?\n|;/)
            : [];
    const seen = new Set();
    const lines = [];
    for (const value of asLines) {
        if (typeof value !== "string")
            continue;
        const line = stripDashes(decodeIngredientText(value)
            .replace(/<[^>]*>/g, " ")
            .replace(/^[\s•*-]+/, "")
            .replace(/\s{2,}/g, " ")
            .trim());
        if (!line)
            continue;
        const key = line.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        lines.push(line);
    }
    return lines;
}
/**
 * Match a leading temporal/conditional conjunction. Only conjunctions
 * that DOUBLE UP with the renderer's "If " / "When " prefix are
 * stripped. Prepositions that are part of a complete trigger phrase
 * ("after dinner", "before brushing teeth", "on waking", "at
 * lights-out") are preserved - they ARE the cue, not a wrapper.
 *
 * Two-word conjunctions go first so the regex prefers "as soon as"
 * over a bare "as".
 */
const LEADING_CONJUNCTION = /^\s*(?:as\s+soon\s+as|the\s+moment|whenever|when|while|once|if)\b[\s,]+/i;
/**
 * Normalise a Gollwitzer if-then trigger phrase. Strips a leading
 * conjunction (when, if, after, …) and lower-cases the first letter
 * so the renderer can prepend "If " or "When " without doubling up.
 *
 * "When you finish brushing your teeth" → "you finish brushing your teeth"
 * "If alarm rings"                     → "alarm rings"
 * "you finish dinner"                  → "you finish dinner" (untouched)
 *
 * Trailing periods are stripped: the renderer adds its own terminator.
 */
export function normaliseTriggerPhrase(input) {
    if (!input)
        return "";
    let s = input.trim();
    // Strip up to two leading conjunctions in case the LLM doubled up
    // ("When if you finish dinner").
    for (let i = 0; i < 2; i++) {
        const next = s.replace(LEADING_CONJUNCTION, "");
        if (next === s)
            break;
        s = next;
    }
    s = s.replace(/[.!?]+$/u, "").trim();
    return s;
}
// A memory value must read in the third person ("Has a son named...");
// first/second person leaks teach agents the wrong register and read oddly on
// the member's own memory map.
const FIRST_SECOND_LEAD = /^(i|i'm|i've|i'd|im|me|we|you|you're)\b/i;
const FIRST_PERSON_I = /(^|[^a-zA-Z])I(['\s]|$)/;
// my/our/your are first or second person wherever they appear
const POSSESSIVE_ANYWHERE = /\b(my|our|your)\b/i;
export function violatesThirdPerson(text) {
    const t = text.trim();
    if (t.length === 0)
        return false;
    return FIRST_SECOND_LEAD.test(t) || FIRST_PERSON_I.test(t) || POSSESSIVE_ANYWHERE.test(t);
}
// A durable memory must be true next month; a claim anchored to a specific
// recent moment is a signal, not a memory, and reads as stale within a day.
const EPHEMERAL_ANCHOR = /\b(last night|tonight|today|yesterday|this (morning|afternoon|evening|week|weekend)|right now|currently)\b/i;
export function isEphemeralClaim(text) {
    return EPHEMERAL_ANCHOR.test(text);
}
// Memory stores facts in the third person (the agent register); the member's
// own screen speaks to them. "they" in a fact value always means the member.
const SECOND_PERSON_SWAPS = [
    ["themselves", "yourself"],
    ["theirs", "yours"],
    ["their", "your"],
    ["they", "you"],
    ["them", "you"],
];
export function toSecondPerson(input) {
    let s = input;
    for (const [from, to] of SECOND_PERSON_SWAPS) {
        s = s.replace(new RegExp(`\\b${from}\\b`, "gi"), (m) => m[0] === m[0]?.toUpperCase() ? `${to[0]?.toUpperCase()}${to.slice(1)}` : to);
    }
    return s;
}
export function englishVariantForUnits(units) {
    return units === "us" ? "us" : "gb";
}
// Sentence-initial and title-case words have to survive the rewrite.
function matchCase(source, replacement) {
    if (source === source.toUpperCase() && source !== source.toLowerCase()) {
        return replacement.toUpperCase();
    }
    return source[0] === source[0]?.toUpperCase()
        ? replacement[0]?.toUpperCase() + replacement.slice(1)
        : replacement;
}
const VARIANT_TERMS = [
    ["well-being", "wellbeing"],
    ["fiber", "fibre"],
    ["fibers", "fibres"],
    ["liter", "litre"],
    ["liters", "litres"],
    ["gray", "grey"],
    ["fueled", "fuelled"],
    ["fueling", "fuelling"],
    ["labeled", "labelled"],
    ["labeling", "labelling"],
    ["traveled", "travelled"],
    ["traveling", "travelling"],
    ["canceled", "cancelled"],
    ["modeling", "modelling"],
    ["center", "centre"],
    ["centers", "centres"],
    ["centered", "centred"],
    ["analyze", "analyse"],
    ["analyzed", "analysed"],
    ["analyzing", "analysing"],
];
const IZE = /\b([A-Za-z]{3,})iz(e|es|ed|ing|er|ers|ation|ations)\b/g;
const IZE_SKIP = /siz(e|es|ed|er|ing)$/i;
const OUR_STEMS = "honor|favor|color|flavor|behavior|savor|neighbor|labor|humor|vigor|odor|harbor|endeavor|rumor|splendor";
const OR_TO_OUR = new RegExp(`\\b(${OUR_STEMS})(s|ed|ing|ite|ites|ful|less|able|y)?\\b`, "gi");
const OUR_TO_OR = new RegExp(`\\b(${OUR_STEMS.replace(/or\b/g, "our")})(s|ed|ing|ite|ites|ful|less|able|y)?\\b`, "gi");
function usToGb(input) {
    return input
        .replace(IZE, (m, stem, suffix) => IZE_SKIP.test(m) ? m : `${stem}is${suffix}`)
        .replace(OR_TO_OUR, (m, stem, suffix = "") => matchCase(m, `${stem.toLowerCase().replace(/or$/, "our")}${suffix.toLowerCase()}`));
}
function gbToUs(input) {
    return input.replace(OUR_TO_OR, (m, stem, suffix = "") => matchCase(m, `${stem.toLowerCase().replace(/our$/, "or")}${suffix.toLowerCase()}`));
}
const VARIANT_RULES = {
    us: VARIANT_TERMS.map(([us, gb]) => [new RegExp(`\\b${gb}\\b`, "gi"), us]),
    gb: VARIANT_TERMS.map(([us, gb]) => [new RegExp(`\\b${us}\\b`, "gi"), gb]),
};
export function applyEnglishVariant(input, variant, preserve = []) {
    // A member called Gray or Honor is not a spelling to correct.
    const kept = preserve.filter((p) => p.trim().length > 1);
    const masked = kept.reduce((text, word, i) => text.replace(new RegExp(`\\b${escapeRe(word)}\\b`, "g"), MASK(i)), input);
    let out = variant === "gb" ? usToGb(masked) : gbToUs(masked);
    for (const [pattern, replacement] of VARIANT_RULES[variant]) {
        out = out.replace(pattern, (m) => matchCase(m, replacement));
    }
    return kept.reduce((text, word, i) => text.split(MASK(i)).join(word), out);
}
const MASK = (i) => `\u0000hos${i}\u0000`;
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/**
 * A readable list: "sleep", "sleep and steps", "sleep, steps and stress". The
 * repo had six hand-rolled copies of this, differing only in the conjunction
 * and whether they kept the Oxford comma.
 */
export function joinList(items, opts = {}) {
    const { conjunction = "and", oxford = false } = opts;
    if (items.length <= 1)
        return items[0] ?? "";
    if (items.length === 2)
        return `${items[0]} ${conjunction} ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}${oxford ? "," : ""} ${conjunction} ${items.at(-1)}`;
}
const JSON_FENCE_RE = /```(?:json|jsonc|javascript|js)?\s*\n?([\s\S]*?)```/g;
function parsesAsJsonContainer(candidate) {
    try {
        const parsed = JSON.parse(candidate);
        return typeof parsed === "object" && parsed !== null;
    }
    catch {
        return false;
    }
}
/** Index just past the bracket that closes the one at `start`, or -1 if it never closes. */
function jsonBlobEnd(input, start) {
    let depth = 0;
    let inString = false;
    for (let index = start; index < input.length; index += 1) {
        const char = input[index];
        if (inString) {
            if (char === "\\")
                index += 1;
            else if (char === '"')
                inString = false;
            continue;
        }
        if (char === '"')
            inString = true;
        else if (char === "{" || char === "[")
            depth += 1;
        else if (char === "}" || char === "]") {
            depth -= 1;
            if (depth === 0)
                return index + 1;
        }
    }
    return -1;
}
/** `{` or `[` followed by a quoted key or a nested container: JSON, not prose. */
function looksLikeJsonStart(input, index) {
    return /^[{[]\s*(?:["{[]|\]|\})/.test(input.slice(index, index + 3));
}
/**
 * Remove JSON a model pasted into its prose: tool arguments it echoed, a tool
 * result it copied back, a fenced ```json block. The sentences around them
 * stay. A blob only goes when it parses as an object or array, so an ordinary
 * brace in running text survives; a blob still open at the end of the text is
 * a truncated paste and is dropped too.
 */
export function stripEmbeddedJson(input) {
    if (!input || !/[{[]/.test(input))
        return input;
    let text = input.replace(JSON_FENCE_RE, (fence, body) => parsesAsJsonContainer(body.trim()) ? " " : fence);
    let out = "";
    let index = 0;
    while (index < text.length) {
        const char = text[index];
        if ((char === "{" || char === "[") && looksLikeJsonStart(text, index)) {
            const end = jsonBlobEnd(text, index);
            if (end === -1) {
                text = text.slice(0, index);
                break;
            }
            if (parsesAsJsonContainer(text.slice(index, end))) {
                out += " ";
                index = end;
                continue;
            }
        }
        out += char;
        index += 1;
    }
    if (index < text.length)
        out += text.slice(index);
    return out
        .replace(/[ \t]{2,}/g, " ")
        .replace(/ +([.,;:!?])/g, "$1")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
//# sourceMappingURL=text.js.map